import React, { useState } from "react";
import { useCreateMessageAndResponseMutation } from "../../../api/chatApi";
import type { Message } from "./types";

type ChatMessageFormProps = {
  messages: Message[];
  chatId: number;
  onMessageAdded: (newMessage: Message) => void;
};

const GreetingMessage: Message = {
  role: "assistant",
  text: "Hi, how can I help you today?",
  timeStamp: null,
};

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  messages,
  chatId,
  onMessageAdded,
}) => {
  const [inputText, setInputText] = useState<string>("");
  const [createMessage, { isLoading }] = useCreateMessageAndResponseMutation();

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      role: "user",
      text: inputText,
      timeStamp: new Date().toISOString().replace("T", " ").slice(0, 19),
    };
    onMessageAdded(userMessage);
    setInputText("");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
      }).unwrap();

      const responseMessage = response.response;
      const assistantMessage: Message = {
        role: "assistant",
        text: responseMessage?.text || "",
        timeStamp: responseMessage?.timestamp,
      };
      onMessageAdded(assistantMessage);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const renderChatMessage = (
    message: Message,
    index: number
  ): React.JSX.Element => {
    return (
      <li key={index} className="tw-mb-4">
        <div
          className={`tw-p-2 tw-rounded ${
            message.role === "user" ? "tw-bg-blue-100" : "tw-bg-gray-100"
          }`}
        >
          <div className="tw-font-semibold">
            {message.role === "user" ? "You" : "Assistant"}
          </div>
          <div>{message.text}</div>
          {message.timeStamp && (
            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
              {new Date(message.timeStamp).toLocaleString()}
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="tw-relative tw-h-full">
      <ul
        className="tw-h-full tw-overflow-y-auto"
        style={{ scrollbarGutter: "stable" }}
      >
        {messages.length > 0
          ? messages.map((message, index) => renderChatMessage(message, index))
          : renderChatMessage(GreetingMessage, 0)}
      </ul>
      <div className="tw-flex tw-gap-2">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="tw-flex-1 tw-p-2 tw-border tw-rounded"
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={inputText.length === 0 || isLoading}
          className="tw-px-4 tw-py-2 tw-bg-blue-500 tw-text-white tw-rounded disabled:tw-opacity-50"
        >
          {isLoading ? "Sending..." : "Send"}
        </button>
      </div>
    </form>
  );
};
